/** PowerTRX export page (/outages/powertrx): EIA-861 Schedule 3 reliability is
 *  reported from PowerTRX, so a month of reviewed outages is exported in
 *  PowerTRX's "Import Outages" CSV format and uploaded there.
 *
 *  Review first: every closed outage needs a PowerTRX cause before it exports.
 *  The review table sets causes one at a time or in bulk (a storm usually has
 *  one cause for dozens of outages). Setting causes needs the dispatcher role;
 *  downloading needs Outage admin, like the other exports. The server is the
 *  real gate. */
import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { isApiError } from '../../api/client.ts'
import { downloadPowertrxExport } from '../../api/oms.ts'
import { useAuth } from '../../hooks/useAuth.ts'
import { usePatchOutage, usePowertrxPreview } from '../../hooks/useOms.ts'
import type { PowertrxReviewItem } from '../../types/oms.ts'
import { canDispatch, hasCapability } from '../../utils/capabilities.ts'
import { CausePicker } from './CausePicker.tsx'
import { OmsSubNav } from './OmsSubNav.tsx'
import { ScopeBadge, formatDateTime, formatDuration } from './omsFormat.tsx'
import { monthRange, previousMonth } from './powertrxMonth.ts'

function durationOf(item: PowertrxReviewItem): string {
  if (!item.end_time) return 'ongoing'
  return formatDuration((Date.parse(item.end_time) - Date.parse(item.start_time)) / 1000)
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] text-gray-400 uppercase tracking-wide">{label}</div>
      <div className="text-lg font-semibold text-gray-800">{value}</div>
    </div>
  )
}

function ReviewTable({ items, canEdit }: { items: PowertrxReviewItem[]; canEdit: boolean }) {
  const patch = usePatchOutage()
  const [rowCause, setRowCause] = useState<Record<number, string | null>>({})
  const [checked, setChecked] = useState<Set<number>>(new Set())
  const [bulkCause, setBulkCause] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  async function save(ids: number[], cause: string) {
    setBusy(true)
    setMessage(null)
    const failed: number[] = []
    for (const id of ids) {
      const item = items.find(i => i.id === id)
      if (!item) continue
      try {
        await patch.mutateAsync({
          id,
          body: { expected_updated_at: item.updated_at, cause_detail: cause },
        })
      } catch {
        failed.push(id)
      }
    }
    setBusy(false)
    setChecked(new Set())
    setMessage(
      failed.length
        ? `Could not save ${failed.length} (changed since loaded?): #${failed.join(', #')}. Reload and retry.`
        : `Cause saved on ${ids.length} outage${ids.length === 1 ? '' : 's'}.`,
    )
  }

  const allChecked = items.length > 0 && checked.size === items.length

  return (
    <div className="space-y-2">
      {canEdit && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-gray-600">Apply to checked ({checked.size}):</span>
          <CausePicker
            value={bulkCause}
            onChange={setBulkCause}
            className="text-sm border border-gray-300 rounded-md px-2 py-1.5"
          />
          <button
            onClick={() => bulkCause && save([...checked], bulkCause)}
            disabled={busy || !bulkCause || checked.size === 0}
            className="text-sm px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            Apply cause
          </button>
        </div>
      )}
      {message && <div className="text-sm text-gray-700 bg-gray-50 rounded px-3 py-2">{message}</div>}
      <div className="overflow-auto border border-gray-100 rounded-md">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr className="border-b border-gray-200">
              {canEdit && (
                <th className="p-2 w-8">
                  <input
                    type="checkbox"
                    aria-label="Check all"
                    checked={allChecked}
                    onChange={() =>
                      setChecked(allChecked ? new Set() : new Set(items.map(i => i.id)))
                    }
                  />
                </th>
              )}
              <th className="p-2">#</th>
              <th className="p-2">Started</th>
              <th className="p-2">Duration</th>
              <th className="p-2">Area</th>
              <th className="p-2">Location</th>
              <th className="p-2 text-right">Customers</th>
              {canEdit && <th className="p-2">Cause</th>}
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.id} className="border-b border-gray-50">
                {canEdit && (
                  <td className="p-2">
                    <input
                      type="checkbox"
                      aria-label={`Check outage ${item.id}`}
                      checked={checked.has(item.id)}
                      onChange={() =>
                        setChecked(prev => {
                          const next = new Set(prev)
                          if (next.has(item.id)) next.delete(item.id)
                          else next.add(item.id)
                          return next
                        })
                      }
                    />
                  </td>
                )}
                <td className="p-2 font-mono text-xs text-gray-500">#{item.id}</td>
                <td className="p-2 whitespace-nowrap">{formatDateTime(item.start_time)}</td>
                <td className="p-2 whitespace-nowrap">{durationOf(item)}</td>
                <td className="p-2"><ScopeBadge scope={item.scope} /></td>
                <td className="p-2">{item.location}</td>
                <td className="p-2 text-right">{item.customers_affected ?? '--'}</td>
                {canEdit && (
                  <td className="p-2">
                    <div className="flex items-center gap-2">
                      <CausePicker
                        value={rowCause[item.id] ?? null}
                        onChange={c => setRowCause(prev => ({ ...prev, [item.id]: c }))}
                        className="text-sm border border-gray-300 rounded-md px-2 py-1"
                      />
                      <button
                        onClick={() => {
                          const cause = rowCause[item.id]
                          if (cause) save([item.id], cause)
                        }}
                        disabled={busy || !rowCause[item.id]}
                        className="text-xs px-2 py-1 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                      >
                        Save
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export function PowertrxExportPage() {
  const { user } = useAuth()
  const canEdit = canDispatch(user?.capabilities)
  const canDownload = hasCapability(user?.capabilities, 'oms.admin')
  const queryClient = useQueryClient()

  const [month, setMonth] = useState(previousMonth())
  const [includeExported, setIncludeExported] = useState(false)
  const [customersServed, setCustomersServed] = useState<string>('')
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)

  const range = useMemo(() => monthRange(month), [month])
  const { data, isLoading, isError, error } = usePowertrxPreview(
    canEdit ? { ...range, include_exported: includeExported } : null,
  )

  // Seed customers served from the server default once it arrives.
  useEffect(() => {
    if (data && customersServed === '') setCustomersServed(String(data.default_customers_served))
  }, [data, customersServed])

  const needsCause = (data?.review ?? []).filter(i => i.reason === 'needs_cause')
  const stillOpen = (data?.review ?? []).filter(i => i.reason === 'still_open')
  const served = Number(customersServed)
  const servedValid = Number.isInteger(served) && served > 0

  async function onDownload() {
    setDownloadError(null)
    setDownloading(true)
    try {
      await downloadPowertrxExport(
        { ...range, total_customers_served: served, include_exported: includeExported },
        `powertrx_outages_${month}.csv`,
      )
      await queryClient.invalidateQueries({ queryKey: ['oms', 'powertrx'] })
    } catch (e) {
      setDownloadError(isApiError(e) ? e.detail : 'Export failed.')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      <div className="max-w-7xl mx-auto p-4 space-y-4">
        <OmsSubNav />
        <div>
          <h2 className="text-lg font-semibold text-gray-800">PowerTRX export</h2>
          <p className="text-sm text-gray-500">
            Reviewed outages in PowerTRX&rsquo;s import format, for EIA reliability
            reporting. Download, then upload the file in PowerTRX under
            Import/Export &rarr; Upload CSV.
          </p>
        </div>

        {!canEdit ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-sm text-gray-500">
            The PowerTRX export needs the Dispatcher role (to review causes) or
            Outage admin (to download).
          </div>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-wrap items-end gap-4">
              <label className="block">
                <span className="text-xs text-gray-500">Month</span>
                <input
                  type="month"
                  value={month}
                  onChange={e => e.target.value && setMonth(e.target.value)}
                  className="mt-1 block text-sm border border-gray-300 rounded-md px-2 py-1.5"
                />
              </label>
              <label className="block">
                <span className="text-xs text-gray-500">Total customers served</span>
                <input
                  type="number"
                  min={1}
                  value={customersServed}
                  onChange={e => setCustomersServed(e.target.value)}
                  className="mt-1 block w-36 text-sm border border-gray-300 rounded-md px-2 py-1.5"
                />
                <span className="block text-[11px] text-gray-400 mt-0.5">
                  Latest EIA-861M customer count
                </span>
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 pb-5">
                <input
                  type="checkbox"
                  checked={includeExported}
                  onChange={e => setIncludeExported(e.target.checked)}
                />
                Include outages already exported
              </label>
            </div>

            {isLoading ? (
              <div className="text-sm text-gray-400 py-8 text-center">Loading…</div>
            ) : isError ? (
              <div className="text-sm text-red-600 py-8 text-center">
                {isApiError(error) ? error.detail : 'Failed to load the export preview.'}
              </div>
            ) : data ? (
              <>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <Stat label="Outages ready" value={data.outages} />
                    <Stat label="PowerTRX rows" value={data.rows} />
                    <Stat label="Customer interruptions" value={data.customers_interrupted} />
                    <Stat
                      label="Customer-minutes"
                      value={data.customer_minutes.toLocaleString()}
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    One row per group of customers restored together, so a staged
                    restoration exports as several rows. Left out: {data.already_exported}{' '}
                    already exported, {data.cancelled} cancelled, {data.no_customer_data}{' '}
                    with no interruption over 5 minutes.
                  </p>
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      onClick={onDownload}
                      disabled={!canDownload || downloading || data.outages === 0 || !servedValid}
                      title={canDownload ? undefined : 'Downloading needs the Outage admin role'}
                      className="text-sm px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {downloading ? 'Preparing…' : `Download PowerTRX CSV (${data.rows} rows)`}
                    </button>
                    {!canDownload && (
                      <span className="text-xs text-gray-500">
                        Downloading needs the Outage admin role.
                      </span>
                    )}
                    {needsCause.length > 0 && (
                      <span className="text-xs text-amber-700">
                        {needsCause.length} outage{needsCause.length === 1 ? '' : 's'} still
                        need a cause and won&rsquo;t be included yet.
                      </span>
                    )}
                  </div>
                  {downloadError && (
                    <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                      {downloadError}
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-2">
                  <h3 className="text-sm font-semibold text-gray-800">
                    Needs a cause ({needsCause.length})
                  </h3>
                  {needsCause.length === 0 ? (
                    <div className="text-sm text-gray-400">Every closed outage has a cause.</div>
                  ) : (
                    <ReviewTable items={needsCause} canEdit={canEdit} />
                  )}
                </div>

                {stillOpen.length > 0 && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-2">
                    <h3 className="text-sm font-semibold text-gray-800">
                      Still open ({stillOpen.length})
                    </h3>
                    <p className="text-xs text-gray-500">
                      Exported once closed. Check these on the Live tab: an outage
                      open for days may need closing or cancelling.
                    </p>
                    <ul className="text-sm text-gray-700 space-y-1">
                      {stillOpen.map(i => (
                        <li key={i.id}>
                          <span className="font-mono text-xs text-gray-500">#{i.id}</span>{' '}
                          {i.location} · since {formatDateTime(i.start_time)} ·{' '}
                          {i.customers_affected ?? '--'} customers
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}
